/*
  Alepu motion. No library.
  - reveals elements on scroll, with per-element stagger
  - drives the transparent-over-hero header state
  - slow parallax on elements marked [data-parallax]
  - splits [data-reveal='lines'] headings so each rendered line masks in
  - tracks the in-view Expedition chapter for the sticky step rail
*/
(function () {
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------- reveals */

  var revealObserver = null;

  function revealAll(root) {
    (root || document).querySelectorAll('[data-reveal]').forEach(function (el) {
      el.classList.add('is-revealed');
    });
  }

  function observeReveals(root) {
    var nodes = (root || document).querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!nodes.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      revealAll(root);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.08 },
      );
    }

    nodes.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /*
    Elements sharing a [data-reveal-group] come in one after another rather
    than all at once. 90ms reads as considered; much more reads as a slideshow.
  */
  function applyStagger(root) {
    (root || document).querySelectorAll('[data-reveal-group]').forEach(function (group) {
      var step = parseInt(group.getAttribute('data-reveal-step'), 10) || 90;
      var children = group.querySelectorAll(':scope > [data-reveal]');
      children.forEach(function (child, i) {
        child.style.setProperty('--alepu-reveal-delay', i * step + 'ms');
      });
    });
  }

  /* --------------------------------------------------------- line reveals */

  /*
    Splits a heading into its *rendered* lines so each can mask in separately.
    Words are measured first, then grouped by offsetTop — there is no other way
    to know where the browser broke a clamp()-sized line.

    Every node is built with createElement + textContent. Headings render
    through `| escape`, so reading textContent decodes the entities; writing
    that string back through innerHTML would re-interpret it and break on
    apostrophes. See context/decisions.md, 2026-08-21.
  */
  var originalText = new WeakMap();
  var LINE_STEP = 90;

  function splitLines(el) {
    if (!originalText.has(el)) originalText.set(el, el.textContent);

    var words = originalText.get(el).split(/\s+/).filter(Boolean);
    if (!words.length) return;

    /* 1. one span per word, so each has a measurable box */
    el.textContent = '';
    var probes = words.map(function (word, i) {
      var span = document.createElement('span');
      span.textContent = word;
      el.appendChild(span);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      return span;
    });

    /* 2. group by vertical position */
    var lines = [];
    var top = null;
    probes.forEach(function (span) {
      if (top === null || Math.abs(span.offsetTop - top) > 1) {
        lines.push([]);
        top = span.offsetTop;
      }
      lines[lines.length - 1].push(span.textContent);
    });

    /* 3. rebuild as masked lines */
    el.textContent = '';
    lines.forEach(function (line, i) {
      var outer = document.createElement('span');
      outer.className = 'alepu-line';
      var inner = document.createElement('span');
      inner.className = 'alepu-line__inner';
      inner.textContent = line.join(' ');
      inner.style.setProperty('--alepu-line-delay', i * LINE_STEP + 'ms');
      outer.appendChild(inner);
      el.appendChild(outer);
    });
  }

  function splitAll(root) {
    if (reduceMotion.matches) return;
    (root || document).querySelectorAll("[data-reveal='lines']").forEach(splitLines);
  }

  /* Restores the plain text, for when reduced motion is switched on live. */
  function unsplitAll() {
    document.querySelectorAll("[data-reveal='lines']").forEach(function (el) {
      if (originalText.has(el)) el.textContent = originalText.get(el);
    });
  }

  /* -------------------------------------------------------------- header */

  function headerHeight() {
    var wrapper = document.querySelector('.header-wrapper');
    if (!wrapper) return 0;
    /* The section wrapper, not the bar, is what occupies flow space. */
    var section = wrapper.closest('.shopify-section') || wrapper;
    return section.offsetHeight || wrapper.offsetHeight || 0;
  }

  /* The sticky product column offsets against this, so it clears the bar. */
  function publishHeaderHeight() {
    document.documentElement.style.setProperty('--alepu-header-h', headerHeight() + 'px');
  }

  /* ------------------------------------------------------------ parallax */

  var parallaxItems = [];

  function collectParallax() {
    parallaxItems = [].slice.call(document.querySelectorAll('[data-parallax]'));
  }

  function updateParallax() {
    if (reduceMotion.matches) return;
    var viewport = window.innerHeight;

    parallaxItems.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > viewport + 200) return;

      /* The setting is a percentage (0-25), not a multiplier. */
      var depth = (parseFloat(el.getAttribute('data-parallax')) || 0) / 100;
      if (!depth) return;

      /* -1 above the fold, +1 below it */
      var progress = (rect.top + rect.height / 2 - viewport / 2) / (viewport / 2 + rect.height / 2);
      /* Drift stays well inside the 12% oversize on the image wrapper. */
      var shift = progress * depth * rect.height * 0.5;
      el.style.transform = 'translate3d(0, ' + shift.toFixed(2) + 'px, 0)';
    });
  }

  /* ------------------------------------------------------- chapter rail */

  /*
    Tracks whichever chapter is nearest the middle of the viewport and writes
    its index into the sticky rail. Not a scroll listener — the chapters hold
    video facades and their heights change when a film is opened.
  */
  var chapterObservers = new WeakMap();

  function bindChapterRail(root) {
    (root || document).querySelectorAll('[data-chapter-rail]').forEach(function (rail) {
      var section = rail.closest('.alepu-chapters');
      var chapters = section ? section.querySelectorAll('[data-chapter-index]') : [];
      if (!chapters.length) return;

      var current = rail.querySelector('[data-chapter-current]');
      var name = rail.querySelector('[data-chapter-name]');
      var fill = rail.querySelector('[data-chapter-progress]');

      function setActive(chapter) {
        var index = chapter.getAttribute('data-chapter-index');
        if (current) current.textContent = index;
        if (name) name.textContent = chapter.getAttribute('data-chapter-destination') || '';
        if (fill) fill.style.height = (parseInt(index, 10) / chapters.length) * 100 + '%';
      }

      setActive(chapters[0]);

      if (!('IntersectionObserver' in window)) return;

      /* One observer per rail, replaced on section reload, so re-binding one
         chapters section never unobserves another's. */
      var previous = chapterObservers.get(rail);
      if (previous) previous.disconnect();

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) setActive(entry.target);
          });
        },
        /* A band across the middle of the viewport: whichever chapter crosses
           it is the one being read. */
        { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
      );

      chapters.forEach(function (chapter) {
        observer.observe(chapter);
      });
      chapterObservers.set(rail, observer);
    });
  }

  /* ----------------------------------------------------------------- run */

  var ticking = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      updateParallax();
      ticking = false;
    });
  }

  var resplitTimer = null;

  /*
    The type scale is clamp()-based, so line breaks move as the window moves.
    Re-split on a trailing debounce only — measuring every frame of a drag
    would reflow the whole heading each time. The header/parallax path below
    stays undebounced; it is cheap.
  */
  function onResize() {
    publishHeaderHeight();
    onScroll();

    if (reduceMotion.matches) return;
    if (resplitTimer) clearTimeout(resplitTimer);
    resplitTimer = setTimeout(function () {
      resplitTimer = null;
      splitAll();
    }, 150);
  }

  function init() {
    publishHeaderHeight();
    applyStagger();
    splitAll();
    observeReveals();
    collectParallax();
    bindChapterRail();
    updateParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });

  /* Theme Editor: sections are added, removed and reordered live. */
  document.addEventListener('shopify:section:load', function (event) {
    applyStagger(event.target);
    splitAll(event.target);
    revealAll(event.target);
    bindChapterRail(event.target);
    publishHeaderHeight();
    collectParallax();
  });

  document.addEventListener('shopify:section:reorder', function () {
    collectParallax();
  });

  reduceMotion.addEventListener('change', function () {
    if (reduceMotion.matches) {
      unsplitAll();
      revealAll();
    } else {
      splitAll();
    }
  });
})();
